import { useMemo, type CSSProperties } from 'react';
import type { Participant } from '../../../../types';
import type { ColumnConfig, SortConfig } from '../../../../hooks';
import { ConfigurableTable } from '../../../../components/common';
import { renderCellContent, type FormBaseUrls } from './ParticipantCellRenderers';
import { getColumnMinWidth } from './participantColumnConfig';
import {
  groupCoachingSubTypeOf,
  modalitiesPresent,
  modalityAttendancePrefix,
  showsGroupCoaching,
} from '../../../../utils/attendanceModalityLabels';

/** MED-1442: the 1:1 and W columns, keyed by the bucket each one shows. */
const MODALITY_COLUMNS: Record<string, 'one_on_one' | 'workshop'> = {
  attendance_one_on_one: 'one_on_one',
  attendance_workshop: 'workshop',
};

export interface ParticipantsTableViewProps {
  /** Whether data is loading */
  loading: boolean;
  /** Filtered participant list */
  participants: Participant[];
  /** Visible + hidden column config */
  columnConfig: ColumnConfig[];
  /** Current sort config */
  sortConfig: SortConfig | null;
  /** Sort handler */
  onSort: (key: string) => void;
  /** Total visible column count (for colSpan on empty state) */
  visibleColumnCount: number;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Clear all filters */
  onClearFilters: () => void;
  // Pagination
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Row click opens the participant detail modal */
  onRowClick: (participant: Participant) => void;
  /** Base form URLs for feedback surveys (midpoint, endpoint, post_session) */
  formBaseUrls?: FormBaseUrls;
}

export function ParticipantsTableView({
  loading,
  participants,
  columnConfig,
  sortConfig,
  onSort,
  visibleColumnCount,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  totalPages: _totalPages,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  formBaseUrls,
}: ParticipantsTableViewProps) {
  const getRowStyle = (participant: Participant): CSSProperties | undefined => {
    const isWithdrawn = participant.status === 'withdrawn';
    const style: CSSProperties = {};
    if (participant.has_open_flag && !isWithdrawn) {
      style.backgroundColor = '#FEF3E7';
    }
    // MED-1367: grey text instead of a faded row, so withdrawn rows stay
    // readable. The avatar fades in the name cell.
    if (isWithdrawn) {
      style.color = '#666666';
    }
    return Object.keys(style).length > 0 ? style : undefined;
  };

  // MED-1442: hide the 1:1 and W columns when no row has that bucket, hide
  // GC in a 1:1-only cohort, and
  // label the group coaching columns "PG" for a peer led cohort.
  const { columns, hiddenCount } = useMemo(() => {
    const present = new Set(modalitiesPresent(participants));
    const gc = modalityAttendancePrefix('group_coaching', groupCoachingSubTypeOf(participants));
    const showGc = showsGroupCoaching(participants);
    const shown = columnConfig.filter(
      c =>
        (!MODALITY_COLUMNS[c.id] || present.has(MODALITY_COLUMNS[c.id])) &&
        (showGc || (c.id !== 'attendance' && c.id !== 'sessions'))
    );
    const hidden = columnConfig.filter(c => c.visible && !shown.includes(c)).length;
    return {
      columns: shown.map(c => {
        if (c.id === 'attendance') return { ...c, label: `${gc} attendance` };
        if (c.id === 'sessions') return { ...c, label: `${gc} sessions` };
        return c;
      }),
      hiddenCount: hidden,
    };
  }, [columnConfig, participants]);

  return (
    <ConfigurableTable<Participant>
      data={participants}
      columns={columns}
      getRowKey={(p) => p.id}
      renderCell={(participant, column) =>
        renderCellContent(column, participant, formBaseUrls)
      }
      sortConfig={sortConfig}
      onSort={onSort}
      loading={loading}
      page={page}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      visibleColumnCount={visibleColumnCount - hiddenCount}
      emptyMessage="No participants match the current filters"
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      stickyFirstColumn
      getColumnMinWidth={getColumnMinWidth}
      getRowStyle={getRowStyle}
      onRowClick={onRowClick}
      entityLabel="participants"
    />
  );
}
